import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const MailingAddress = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Where to file Form 990 Returns | Form 990 Mailing Address"
          description="Find the correct IRS mailing address for submitting Form 990. Ensure your return is sent to the right location to avoid delays or rejections."
          keywords=""
          canonical="resources/tax-exempt/form-990-mailing-address/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Mailing Address </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          The IRS has made E-filing mandatory for all Form 990 returns!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=mailaddress`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-mailing-address/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Mailing Address at a Glance
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Since 2019, the IRS requires nearly all organizations to
                    e-file Form 990 and its related forms rather than mail a
                    paper copy. Paper filing is now allowed only in limited
                    exception cases approved by the IRS.
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          E-filing Mandate:
                        </strong>{' '}
                        Electronic filing has been mandatory for Form 990, Form
                        990-EZ , Form 990-PF, and Form 990-T since the Taxpayer
                        First Act took effect in 2019.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Paper Filing Exceptions:
                        </strong>{' '}
                        Short tax years, organizations filing fewer than 250
                        returns, certain religious, governmental, and political
                        organizations, and IRS-approved hardship waivers may
                        still qualify to file on paper.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Mailing Address:
                        </strong>{' '}
                        Organizations approved to paper-file must mail their
                        return to the IRS Service Center in Ogden, UT - the
                        exact address depends on whether the organization is
                        based in the U.S. or a foreign country/possession.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  IRS mandates E-filing for Form 990
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  On July 1, 2019, the IRS mandated electronic filing for all
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    {' '}
                    Form 990 series
                  </Link>
                  , as enacted as{' '}
                  <span style={{ fontWeight: 600 }}>Taxpayer First Act</span>.
                  As a result, organizations can no longer submit paper returns
                  for 990 forms and must file all 990 forms must be filed
                  electronically.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The electronic filing requirements for all 990 forms are as
                  follows:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.3 } }}>
                  <li>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-EZ
                    </Link>
                    : Must be filed electronically for tax years ending{' '}
                    <span style={{ fontWeight: 600 }}>July 31, 2021</span>, and
                    later.{' '}
                  </li>
                  <li>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-pf/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-PF
                    </Link>
                    : Must be e-filed for tax years ending{' '}
                    <span style={{ fontWeight: 600 }}>July 31, 2020</span>, and
                    later.{' '}
                  </li>
                  <li>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990
                    </Link>
                    : Must be filed electronically for tax years ending{' '}
                    <span style={{ fontWeight: 600 }}>July 31, 2020</span>, and
                    later.{' '}
                  </li>
                  <li>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-t/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-T
                    </Link>
                    : Must be electronic filing for tax years beginning after{' '}
                    <span style={{ fontWeight: 600 }}>July 1, 2019</span>.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}> Note:</strong>{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=mailaddress`}
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990-N(e-postcard)
                  </Link>{' '}
                  submitted electronically, there are{' '}
                  <span style={{ fontWeight: 600 }}> no paper forms</span>.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Mailing address for Form 990
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Paper filing for{' '}
                  <span style={{ fontWeight: 600 }}> Form 990</span> is only
                  allowed in rare circumstances, such as when an organization
                  has received an IRS-approved waiver or qualifies under
                  specific exception cases.
                </Typography>
                <Typography variant="body1">
                  The specific exceptions that permit paper filing
                  includes:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Short tax years:</span>{' '}
                    Most{' '}
                    <span style={{ fontWeight: 600 }}> Forms 990, 990-EZ,</span>{' '}
                    and <span style={{ fontWeight: 600 }}> 990-PF</span> for
                    short years must be paper-filed.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Organizations with fewer than 250 total returns filed:
                    </span>{' '}
                    Organizations that file{' '}
                    <span style={{ fontWeight: 600 }}>
                      fewer than 250 returns
                    </span>{' '}
                    of any type (including employment tax, and information
                    returns such as Forms 1099 and W-2) for the calendar year
                    ending within their fiscal year may file{' '}
                    <span style={{ fontWeight: 600 }}>
                      paper Forms 990, 990-EZ
                    </span>
                    , and
                    <span style={{ fontWeight: 600 }}> 990-PF</span>.{' '}
                  </li>
                  <li style={{ fontWeight: 600 }}>
                    Certain religious organizations:{' '}
                  </li>
                  <ul>
                    <li>
                      Churches, interchurch organizations of local units of a
                      church, conventions or associations of churches, or
                      integrated auxiliaries of a church{' '}
                    </li>
                    <ul>
                      <li>
                        ( <span style={{ fontWeight: 600 }}>e.g</span>.,
                        men&apos;s or women&apos;s organizations, religious
                        schools, mission societies, youth groups){' '}
                      </li>
                    </ul>
                    <li>
                      Church-affiliated organizations exclusively engaged in
                      managing funds or maintaining retirement programs as
                      described in IRS Rev. Proc. 96-10.{' '}
                    </li>
                    <li>
                      Schools below college level affiliated with a church or
                      operated by a religious order as per IRS regulations.{' '}
                    </li>
                    <li>
                      Mission societies sponsored by or affiliated with churches
                      if more than half their activities are conducted in or
                      directed at persons in foreign countries.{' '}
                    </li>
                    <li>
                      Exclusively religious activities of any religious order
                      described in IRS Rev. Proc. 91-20.{' '}
                    </li>
                  </ul>
                  <li style={{ fontWeight: 600 }}>
                    Certain governmental organizations:
                  </li>
                  <ul>
                    <li>
                      State institutions whose income is excluded under IRC
                      section 115.
                    </li>
                    <li>
                      Governmental units or affiliates described in IRS Rev.
                      Proc. 95-48.{' '}
                    </li>
                    <li>
                      Organizations described in section 501(c)(1), which are
                      instrumentalities of the United States exempt from federal
                      income tax.{' '}
                    </li>
                  </ul>
                  <li style={{ fontWeight: 600 }}>
                    Certain Political organizations:
                  </li>
                  <ul>
                    <li>State or local committees of political parties, </li>
                    <li>Political committees of state or local candidates, </li>
                    <li>
                      Caucuses or associations of state or local officials,{' '}
                    </li>
                    <li>
                      Organizations required to report under the Federal
                      Election Campaign Act of 1971 as political
                      committees.{' '}
                    </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Organizations with gross receipts normally $50,000 or
                      less:
                    </span>
                    <br />
                    Organizations generally file{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=mailaddress`}
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-N
                    </Link>{' '}
                    electronically, but if they choose to file a full <br />
                    <span style={{ fontWeight: 600 }}>Form 990</span> or{' '}
                    <span style={{ fontWeight: 600 }}>990-EZ</span>, they may be
                    allowed to file on paper if they meet certain conditions.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      IRS-approved waivers:
                    </span>{' '}
                    Organizations that have obtained a specific waiver from the
                    IRS due to inability or hardship in filing electronically
                    may file paper returns.{' '}
                  </li>
                </Box>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              borderRight: '1px solid #828282',
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Organization Location
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 200,
                              textAlign: 'left',
                              fontWeight: 600,
                              fontSize: 16,
                            }}
                          >
                            Mailing Address
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            United States (Domestic)
                          </TableCell>

                          <TableCell sx={{ textAlign: 'left' }}>
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Department of the Treasury
                            </span>{' '}
                            <br />
                            Internal Revenue Service Center <br />
                            Ogden, UT 84201-0027
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Foreign country or U.S. possession
                          </TableCell>

                          <TableCell sx={{ textAlign: 'left' }}>
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Department of the Treasury
                            </span>{' '}
                            <br />
                            Internal Revenue Service Center
                            <br />
                            P.O. Box 409101
                            <br />
                            Ogden, UT 84409
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Alert
                  severity="info"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>
                    {' '}
                    Important Update:
                  </strong>{' '}
                  The IRS now requires all Form 990 filings to be submitted
                  electronically. Paper submissions are
                  <span style={{ fontWeight: 600 }}> no longer accepted</span> .
                </Alert>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 8868
                  </Link>{' '}
                  (Application for Extension) can be filed either electronically
                  or on paper. If an organization chooses to file by mail, the
                  extension request should be sent to the appropriate IRS
                  address.
                </Typography>
                <Box
                  sx={{
                    maxWidth: 410,
                    mb: 3,
                    p: 3,
                    border: '8px solid transparent',
                    borderRadius: 3,
                    background:
                      'linear-gradient(#fcf6bd) padding-box, repeating-linear-gradient(-45deg, #C8102E 0, #C8102E 12.5%, #ffffff 0, #ffffff 25%, #3492b8 0, #3492b8 37.5%, #ffffff 0, #ffffff 50%) 0 / 5em 5em',
                  }}
                >
                  <Typography variant="body1">
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Mail to the address if you’re filing on paper
                    </span>{' '}
                    <br />
                    Internal Revenue Service
                    <br />
                    Mail Stop 6054
                    <br />
                    1973 N Rulon White Blvd
                    <br />
                    Ogden, UT 84201-0045
                  </Typography>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}> Note :</strong> If
                  requesting an extension for{' '}
                  <span style={{ fontWeight: 600 }}> Form 8870</span>, you must
                  file a paper{' '}
                  <span style={{ fontWeight: 600 }}>Form 8868</span> to this
                  address.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-file Form 990 with TaxZerone:
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  With the help of TaxZerone, you can file the 990 forms easily
                  and securely with advanced encrypted security
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    TaxZerone supports e-filing for all 990 forms ({' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=mailaddress`}
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      990-N
                    </Link>
                    ,{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      990-EZ
                    </Link>
                    ,{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-pf/"
                      sx={{ color: '#0202ff' }}
                    >
                      990-PF
                    </Link>
                    ,{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      990
                    </Link>
                    ,{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-t/"
                      sx={{ color: '#0202ff' }}
                    >
                      990-T
                    </Link>{' '}
                    and{' '}
                    <Link
                      href="/tax-exempt/e-file-form-990-amendments/"
                      sx={{ color: '#0202ff' }}
                    >
                      Amendments
                    </Link>
                    ){' '}
                  </li>
                  <li>
                    It also supports the e-filing forms of{' '}
                    <Link
                      href="/tax-exempt/efile-ftb-ca-form199-online/"
                      sx={{ color: '#0202ff' }}
                    >
                      CA Form 199
                    </Link>
                    , and the extension{' '}
                    <Link
                      href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8868
                    </Link>
                    .{' '}
                  </li>
                  <li>
                    It also includes the applicable{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-schedules/"
                      sx={{ color: '#0202ff' }}
                    >
                      990 Schedules
                    </Link>{' '}
                    based on the information you entered.{' '}
                  </li>
                  <li>
                    Upload and process multiple tax returns at once, saving time
                    for organizations with several filings.{' '}
                  </li>
                  <li>
                    File current and prior year returns, making it easy to
                    manage compliance for multiple years.{' '}
                  </li>
                  <li>
                    Our customer support is available to answer questions and
                    resolve issues quickly.{' '}
                  </li>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, please refer to the
                        <Link
                          href="https://www.irs.gov/charities-non-profits/annual-electronic-notice-form-990-n-for-small-organizations-faqs-how-to-file"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions
                        </Link>{' '}
                        of Form 990-N,{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions
                        </Link>{' '}
                        of Form 990,{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990ez.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions
                        </Link>{' '}
                        of 990-EZ,{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990pf.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions
                        </Link>{' '}
                        of 990-PF,{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990t.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions
                        </Link>{' '}
                        of 990-T and{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i8868.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instructions
                        </Link>{' '}
                        on 8868.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File Your Form 990?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              E-file your Form 990 quickly, securely, and stress-free with
              TaxZerone!
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=mailaddress`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default MailingAddress;
